/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import cx from 'classnames';
import type { SupersetTheme } from '@superset-ui/core';
import { css, styled, addAlpha } from '@superset-ui/core';
import Popconfirm from 'src/components/Popconfirm';
import { Tooltip } from 'src/components/Tooltip';
import type { PopconfirmProps } from 'antd/es/popconfirm';
import type { TooltipPlacement } from 'antd/lib/tooltip';

export const LIST_ACTION_BUTTON_CLASS = 'action-button';

/** List row / toolbar icon actions live under this container in ListView & aligned surfaces. */
export const LIST_ACTION_SCOPE_CLASS = 'actions';

export const getListActionButtonBoxSize = (
  iconSize: number,
  gridUnit: number,
) => iconSize + gridUnit * 2;

const listActionButtonCss = ({
  theme,
  $disabled,
  $iconSize,
}: {
  theme: SupersetTheme;
  $disabled?: boolean;
  $iconSize?: number;
}) => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${getListActionButtonBoxSize($iconSize ?? 16, theme.gridUnit)}px;
  height: ${getListActionButtonBoxSize($iconSize ?? 16, theme.gridUnit)}px;
  padding: 0;
  border-radius: ${theme.borderRadius}px;
  color: ${theme.colors.grayscale.base};
  border: none;
  background: transparent;
  cursor: ${$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${$disabled ? 0.45 : 1};
  pointer-events: ${$disabled ? 'none' : 'auto'};
  line-height: 0;
  flex-shrink: 0;
  vertical-align: middle;
  transition: color 0.15s ease, background-color 0.15s ease;

  .anticon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: ${$iconSize ?? 16}px;
    line-height: 0;
    vertical-align: middle;
    color: inherit;
  }

  svg {
    display: block;
    width: ${$iconSize ?? 16}px;
    height: ${$iconSize ?? 16}px;
    font-size: ${$iconSize ?? 16}px;
  }

  ${!$disabled &&
  css`
    &:hover:not(:disabled):not([disabled]):not([data-disabled='true']) {
      color: ${theme.colors.primary.base};
      background: ${addAlpha(theme.colors.primary.base, 0.12)};

      .anticon {
        color: inherit;
      }

      svg path {
        fill: currentColor;
      }
    }
  `}

  &:disabled,
  &[disabled],
  &[data-disabled='true'] {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

/** Global fallback for icon-only .action-button inside .actions (List rows, download center, etc.). */
export const getListActionButtonGlobalCss = (theme: SupersetTheme) => {
  const defaultBox = getListActionButtonBoxSize(16, theme.gridUnit);
  const largeBox = getListActionButtonBoxSize(20, theme.gridUnit);
  const scoped = `.${LIST_ACTION_SCOPE_CLASS} .${LIST_ACTION_BUTTON_CLASS}`;

  return css`
    ${scoped} {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      width: ${defaultBox}px;
      height: ${defaultBox}px;
      padding: 0;
      border-radius: ${theme.borderRadius}px;
      color: ${theme.colors.grayscale.base};
      border: none;
      background: transparent;
      cursor: pointer;
      line-height: 0;
      flex-shrink: 0;
      vertical-align: middle;
      transition: color 0.15s ease, background-color 0.15s ease;
    }

    ${scoped} .anticon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      line-height: 0;
      vertical-align: middle;
      color: inherit;
    }

    ${scoped} svg {
      display: block;
      width: 16px;
      height: 16px;
      font-size: 16px;
    }

    ${scoped}[data-icon-size='20'] {
      width: ${largeBox}px;
      height: ${largeBox}px;
    }

    ${scoped}[data-icon-size='20'] svg,
  ${scoped}[data-icon-size='20'] .anticon {
      width: 20px;
      height: 20px;
      font-size: 20px;
    }

    ${scoped}:hover:not(:disabled):not([disabled]):not([data-disabled='true']) {
      color: ${theme.colors.primary.base};
      background: ${addAlpha(theme.colors.primary.base, 0.12)};
    }

    ${scoped}:hover:not(:disabled):not([disabled]):not([data-disabled='true']) .anticon {
      color: inherit;
    }

    ${scoped}:hover:not(:disabled):not([disabled]):not([data-disabled='true']) svg path {
      fill: currentColor;
    }

    ${scoped}:disabled,
    ${scoped}[disabled],
  ${scoped}[data-disabled='true'] {
      opacity: 0.45;
      cursor: not-allowed;
      pointer-events: none;
    }
  `;
};

/** Stable hit target when wrapping ListAction* inside Tooltip / Popconfirm. */
export const ListActionTooltipTrigger = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 0;
  vertical-align: middle;
`;

export const ListActionButtonGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.gridUnit}px;
  white-space: nowrap;
`;

export const ListActionButton = styled.button<{
  $disabled?: boolean;
  $iconSize?: number;
}>`
  ${listActionButtonCss}
`;

export const ListActionIconButton = styled.span<{
  $disabled?: boolean;
  $iconSize?: number;
}>`
  ${listActionButtonCss}
`;

export type ListActionIconTriggerPopconfirm = Pick<
  PopconfirmProps,
  | 'title'
  | 'onConfirm'
  | 'okText'
  | 'cancelText'
  | 'placement'
  | 'disabled'
  | 'okButtonProps'
>;

export type ListActionIconTriggerProps = {
  id?: string;
  title?: string;
  placement?: TooltipPlacement;
  icon: React.ReactElement;
  className?: string;
  iconSize?: number;
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
  popconfirm?: ListActionIconTriggerPopconfirm;
};

/** Tooltip > Popconfirm > icon button — aligned with List operation column */
export function ListActionIconTrigger({
  id,
  title,
  placement = 'bottom',
  icon,
  className,
  iconSize,
  disabled,
  onClick,
  popconfirm,
}: ListActionIconTriggerProps): React.ReactElement {
  const button = (
    <ListActionIconButton
      role="button"
      tabIndex={disabled ? -1 : 0}
      className={cx(LIST_ACTION_BUTTON_CLASS, className)}
      $disabled={disabled}
      $iconSize={iconSize}
      data-disabled={disabled ? 'true' : undefined}
      data-icon-size={
        iconSize && iconSize !== 16 ? String(iconSize) : undefined
      }
      onClick={onClick}
    >
      {icon}
    </ListActionIconButton>
  );

  let trigger: React.ReactNode = button;
  if (popconfirm) {
    trigger = (
      <Popconfirm
        title={popconfirm.title}
        onConfirm={popconfirm.onConfirm}
        okText={popconfirm.okText}
        cancelText={popconfirm.cancelText}
        placement={popconfirm.placement}
        disabled={popconfirm.disabled || disabled}
        okButtonProps={popconfirm.okButtonProps}
      >
        {button}
      </Popconfirm>
    );
  }

  if (!title) {
    return trigger as React.ReactElement;
  }

  return (
    <Tooltip id={id} title={title} placement={placement}>
      <ListActionTooltipTrigger>{trigger}</ListActionTooltipTrigger>
    </Tooltip>
  );
}

export type ListActionButtonTriggerProps = {
  id?: string;
  title?: string;
  placement?: TooltipPlacement;
  children: React.ReactElement;
  className?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit';
};

/** Tooltip > button — for toolbar icon buttons */
export function ListActionButtonTrigger({
  id,
  title,
  placement = 'bottom',
  children,
  className,
  disabled,
  onClick,
  type = 'button',
}: ListActionButtonTriggerProps): React.ReactElement {
  const button = (
    <ListActionButton
      type={type}
      className={cx(LIST_ACTION_BUTTON_CLASS, className)}
      disabled={disabled}
      $disabled={disabled}
      onClick={onClick}
    >
      {children}
    </ListActionButton>
  );

  if (!title) {
    return button;
  }

  return (
    <Tooltip id={id} title={title} placement={placement}>
      <ListActionTooltipTrigger>{button}</ListActionTooltipTrigger>
    </Tooltip>
  );
}
